/**
 * QR Code Linking Component
 * Displays a QR code for iOS app to scan and link to participant account
 * Includes WebSocket listener for real-time linking detection
 */

import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Link,
} from "@mui/material";
import { CheckCircle, Refresh, Email, ArrowBack } from "@mui/icons-material";
import { QRCodeSVG } from "qrcode.react";
import { analyticsService } from "../../services/analyticsService";
import type { ReconnectProof } from "../../services/verificationService";

interface QRCodeLinkingProps {
  participantId: string;
  studyCode: string;
  email: string;
  reconnectProof: ReconnectProof;
  onLinkingComplete: () => void;
  onFallbackRequest: () => void;
  onReconnectProofRequired: () => void;
  onBack?: () => void;
}

interface LinkingSessionCredentials {
  sessionId: string;
  websocketToken: string;
  expiresAt: string;
}

type LinkingState = "loading" | "showing_qr" | "linked" | "expired" | "error";

const QRCodeLinking: React.FC<QRCodeLinkingProps> = ({
  participantId,
  studyCode,
  email,
  reconnectProof,
  onLinkingComplete,
  onFallbackRequest,
  onReconnectProofRequired,
  onBack,
}) => {
  const [state, setState] = useState<LinkingState>("loading");
  const [qrData, setQrData] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);
  const [error, setError] = useState<string>("");
  const wsRef = useRef<WebSocket | null>(null);
  const expiryTimerRef = useRef<NodeJS.Timeout | null>(null);
  const pollingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const completionTimerRef = useRef<NodeJS.Timeout | null>(null);
  const activeSessionRef = useRef<LinkingSessionCredentials | null>(null);
  const completionHandledRef = useRef(false);
  const onLinkingCompleteRef = useRef(onLinkingComplete);

  // Keep the callback ref updated
  useEffect(() => {
    onLinkingCompleteRef.current = onLinkingComplete;
  }, [onLinkingComplete]);

  const stopRealtimeListeners = () => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    if (pollingTimerRef.current) {
      clearInterval(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }
  };

  const markLinked = (session: LinkingSessionCredentials, method: 'websocket' | 'polling') => {
    if (
      activeSessionRef.current?.sessionId !== session.sessionId ||
      completionHandledRef.current
    ) {
      return;
    }
    completionHandledRef.current = true;
    stopRealtimeListeners();
    setState("linked");
    analyticsService.trackParticipantHealthkitLinked({ linkingMethod: method });
    completionTimerRef.current = setTimeout(() => onLinkingCompleteRef.current(), 1500);
  };

  const generateToken = async (): Promise<LinkingSessionCredentials | null> => {
    try {
      setState("loading");
      setError("");

      const response = await fetch(
        `${process.env.REACT_APP_API_URL || "http://localhost:3002"}/api/participants/generate-linking-token`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            participantId,
            studyCode,
            reconnectProofId: reconnectProof.proofId,
            reconnectProofToken: reconnectProof.proofToken,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || "Failed to generate linking token");
      }
      if (!data.linkingSessionId || !data.websocketToken || !data.expiresAt) {
        throw new Error("The server did not return a secure linking session");
      }

      const session: LinkingSessionCredentials = {
        sessionId: data.linkingSessionId,
        websocketToken: data.websocketToken,
        expiresAt: data.expiresAt,
      };
      activeSessionRef.current = session;
      completionHandledRef.current = false;

      setQrData(data.qrData);
      setExpiresAt(new Date(data.expiresAt));
      setState("showing_qr");

      // Set up expiry timer
      const timeUntilExpiry = new Date(data.expiresAt).getTime() - Date.now();
      if (expiryTimerRef.current) {
        clearTimeout(expiryTimerRef.current);
      }
      expiryTimerRef.current = setTimeout(() => {
        if (activeSessionRef.current?.sessionId === session.sessionId) {
          stopRealtimeListeners();
          setState("expired");
        }
      }, timeUntilExpiry);
      return session;
    } catch (err: any) {
      activeSessionRef.current = null;
      setError(err.message || "Failed to generate QR code");
      setState("error");
      return null;
    }
  };

  const connectWebSocket = (session: LinkingSessionCredentials) => {
    // Determine WebSocket URL based on API URL
    const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3002";
    const wsProtocol = apiUrl.startsWith("https") ? "wss" : "ws";
    const wsHost = apiUrl.replace(/^https?:\/\//, "");
    const wsUrl = `${wsProtocol}://${wsHost}/ws/linking`;

    try {
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        // Authenticate in the first frame. Keeping the bearer token out of the
        // URL prevents it from entering proxy and browser URL logs.
        ws.send(
          JSON.stringify({
            type: "subscribe",
            sessionId: session.sessionId,
            token: session.websocketToken,
          })
        );
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === "linking_complete" && data.sessionId === session.sessionId) {
            markLinked(session, 'websocket');
          }
        } catch (err) {
          console.error("WebSocket message parse error:", err);
        }
      };

      ws.onerror = (error) => {
        console.error("WebSocket error:", error);
      };

      ws.onclose = () => {};

      wsRef.current = ws;
    } catch (err) {
      console.error("Failed to connect WebSocket:", err);
    }
  };

  // Polling fallback - check if linking was completed
  // This handles cases where WebSocket doesn't work (e.g., iOS app hitting production while frontend is on localhost)
  const checkLinkingStatus = async (session: LinkingSessionCredentials) => {
    if (activeSessionRef.current?.sessionId !== session.sessionId) return;
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || "http://localhost:3002"}/api/participants/check-linking-status`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            linkingSessionId: session.sessionId,
            websocketToken: session.websocketToken,
          }),
        }
      );

      const data = await response.json();

      if (
        data.success &&
        data.linked &&
        data.linkingSessionId === session.sessionId
      ) {
        markLinked(session, 'polling');
      }
    } catch (err) {
      console.error("Error checking linking status:", err);
    }
  };

  const startPolling = (session: LinkingSessionCredentials) => {
    // Poll every 3 seconds as a fallback
    if (pollingTimerRef.current) {
      clearInterval(pollingTimerRef.current);
    }
    pollingTimerRef.current = setInterval(() => checkLinkingStatus(session), 3000);
  };

  // Initialize on mount
  useEffect(() => {
    let disposed = false;
    const initialize = async () => {
      const session = await generateToken();
      if (!disposed && session) {
        connectWebSocket(session);
        startPolling(session);
      }
    };
    initialize();

    return () => {
      disposed = true;
      // Cleanup
      stopRealtimeListeners();
      activeSessionRef.current = null;
      if (expiryTimerRef.current) {
        clearTimeout(expiryTimerRef.current);
        expiryTimerRef.current = null;
      }
      if (completionTimerRef.current) {
        clearTimeout(completionTimerRef.current);
        completionTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRegenerateToken = () => {
    stopRealtimeListeners();
    // The proof was consumed by the first successful generation request.
    // A replacement QR therefore requires a new email verification.
    onReconnectProofRequired();
  };

  // Loading state
  if (state === "loading") {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          py: 4,
        }}
      >
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Generating QR code...
        </Typography>
      </Box>
    );
  }

  // Error state
  if (state === "error") {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          py: 4,
        }}
      >
        <Alert severity="error" sx={{ mb: 3, maxWidth: 400 }}>
          {error}
        </Alert>
        <Button
          variant="contained"
          onClick={handleRegenerateToken}
          startIcon={<Refresh />}
        >
          Try Again
        </Button>
      </Box>
    );
  }

  // Linked state (success)
  if (state === "linked") {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          py: 4,
        }}
      >
        <CheckCircle sx={{ fontSize: 64, color: "success.main", mb: 2 }} />
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          Device Linked Successfully
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Redirecting to complete your profile...
        </Typography>
      </Box>
    );
  }

  // Expired state
  if (state === "expired") {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          py: 4,
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          QR Code Expired
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          The QR code has expired. Please generate a new one.
        </Typography>
        <Button
          variant="contained"
          onClick={handleRegenerateToken}
          startIcon={<Refresh />}
        >
          Generate New QR Code
        </Button>
      </Box>
    );
  }

  // Showing QR state (default)
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        py: 2,
      }}
    >
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
        Link Your Device
      </Typography>

      {/* QR Code */}
      <Box
        sx={{
          p: 3,
          backgroundColor: "white",
          borderRadius: 2,
          boxShadow: 1,
          mb: 3,
        }}
      >
        {qrData && (
          <QRCodeSVG
            value={qrData}
            size={200}
            level="M"
            includeMargin={false}
          />
        )}
      </Box>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
        Scan with the StudySync app
      </Typography>

      {expiresAt && (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 3 }}>
          Code expires in {Math.max(0, Math.ceil((expiresAt.getTime() - Date.now()) / 60000))} minutes
        </Typography>
      )}

      {/* Divider */}
      <Box
        sx={{
          width: "100%",
          maxWidth: 300,
          borderTop: "1px solid",
          borderColor: "divider",
          my: 3,
        }}
      />

      {/* Fallback link */}
      <Link
        component="button"
        variant="body2"
        onClick={onFallbackRequest}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          color: "text.secondary",
          textDecoration: "none",
          "&:hover": {
            textDecoration: "underline",
          },
        }}
      >
        <Email fontSize="small" />
        Need a new QR code? Verify email again
      </Link>

      {/* Back button */}
      {onBack && (
        <Button
          variant="outlined"
          onClick={onBack}
          startIcon={<ArrowBack />}
          sx={{ mt: 3 }}
        >
          Back
        </Button>
      )}
    </Box>
  );
};

export default QRCodeLinking;
